import { asWidgetProps, asTrackingPayload } from 'Src/types/widget-helpers';
import React, { ComponentProps, useState, Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { AccommodationsCategory } from 'Components/lines/product-accommodations-line/productAccommodations.utils';
import { getAllAvailableItems } from 'Components/lines/product-accommodations-line/productAccommodations.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { Button } from 'Components/frameworks/button/Button';
import { Layer } from 'Components/frameworks/layer/Layer';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { Title } from 'Components/frameworks/title/Title';
import { ITEMS_ORDER, sortCategoriesItems } from './ProductRoomsComparator.utils';
import { ProductRoomsComparatorLarge } from './ProductRoomsComparatorLarge';
import { ProductRoomsComparatorNarrow } from './ProductRoomsComparatorNarrow';

const PriceBlock = ({ category, forceDiffPrice, isActive = false }) => {
  if (isActive && !forceDiffPrice) {
    return (
      <div className="text-18 font-bold">
        {category.price?.initialPrice &&
        category.price?.initialPrice?.value !== category.price?.bestPrice?.value ? (
          <span className="pe-5 text-14 font-normal text-sienna line-through">
            {category.price?.initialPrice?.formattedValue}
          </span>
        ) : null}
        {category.price?.bestPrice?.formattedValue}
      </div>
    );
  }

  return <div className="text-18 font-bold">{category.price?.diffPrice}</div>;
};

type Props = {
  accommodationCategories: Array<AccommodationsCategory>;
  activeIndex: number;
  className?: string;
  exclusiveCollectionLabel: string;
  forceDiffPrice?: boolean;
  resortIsExclusiveCollection?: boolean;
};
export const ProductRoomsComparatorLauncher = ({
  accommodationCategories,
  activeIndex,
  className,
  exclusiveCollectionLabel,
  forceDiffPrice = false,
  resortIsExclusiveCollection = false,
}: Props) => {
  const [isLayerOpened, setIsLayerOpened] = useState(false);
  const { isDesktop } = useResponsive();
  const { trackClickEvent } = useTrackEvent();
  const allCategoriesItems = ITEMS_ORDER.map((itemType) =>
    getAllAvailableItems(
      accommodationCategories,
      itemType as 'bathroomEquipments' | 'equipments' | 'services' | 'strengths',
    ),
  );
  const columns = accommodationCategories.map((category, index) => {
    const hasPrice = category.price?.bestPrice || forceDiffPrice;
    return {
      title: `${category.categoryLabel} ${category.exclusiveCollection && !resortIsExclusiveCollection ? ' ' + exclusiveCollectionLabel : ''}`,
      picture: category.media.photos[0],
      price: hasPrice ? (
        <PriceBlock
          {...asWidgetProps<ComponentProps<typeof PriceBlock>>({
            category: category,
            forceDiffPrice: forceDiffPrice,
            isActive: index === activeIndex,
          })}
        />
      ) : null,
      isExclusiveCollection: category.exclusiveCollection || resortIsExclusiveCollection,
      isExclusiveCollectionSpace: category.exclusiveCollection && !resortIsExclusiveCollection,
      surface:
        category.area?.min !== category.area?.max ? (
          <TemplateString
            {...asWidgetProps<ComponentProps<typeof TemplateString>>({
              template: t('common.accommodations.sizeFrom'),
              values: {
                min_area: `${category.area.min}${category.area.unit}`,
                max_area: `${category.area.max}${category.area.unit}`,
              },
            })}
          />
        ) : (
          `${category.area?.max}${category.area?.unit}`
        ),

      capacity: (
        <TemplateString
          {...asWidgetProps<ComponentProps<typeof TemplateString>>({
            template: t('common.accommodations.capacityFrom'),
            values: {
              min_capacity: `${category.minCapacity}`,
              max_capacity: `${category.maxCapacity}`,
            },
          })}
        />
      ),

      strengths: getAllAvailableItems([category], 'strengths').slice(0, 4),
    };
  });

  const getAvailabilityBoard = (set, itemType) => {
    const availableInCategory = new Set(set.map(({ id }) => id));
    return allCategoriesItems[ITEMS_ORDER.indexOf(itemType)]
      .sort(sortCategoriesItems)
      .map(({ id }) => {
        return availableInCategory.has(id);
      });
  };

  const availabilityBoards = accommodationCategories.map((category) => {
    const availableEquipments = category.equipments;
    const availableServices = category.services;
    const availableBathroomEquipments = category.bathroomEquipments;
    const equipmentsAvailability = getAvailabilityBoard(availableEquipments, 'equipments');
    const servicesAvailability = getAvailabilityBoard(availableServices, 'services');
    const bathroomEquipmentsAvailability = getAvailabilityBoard(
      availableBathroomEquipments,
      'bathroomEquipments',
    );
    return {
      equipments: equipmentsAvailability,
      services: servicesAvailability,
      bathroomEquipments: bathroomEquipmentsAvailability,
    };
  });
  return (
    <Fragment>
      <Button
        {...asWidgetProps<ComponentProps<typeof Button>>({
          className: classnames('w-full', className),
          iconLeft: {
            name: ICONS.COMPARE,
          },
          onClick: () => {
            setIsLayerOpened(true);
            trackClickEvent(
              TRACK_EVENT_NAMES.CLICK_ACCOMMODATION,
              asTrackingPayload({
                detail_click: 'compare_rooms',
              }),
            );
          },
        })}
      >
        {t('common.accommodations.compareRooms')}
      </Button>
      {isLayerOpened && (
        <Layer
          {...asWidgetProps<ComponentProps<typeof Layer>>({
            ariaLabel: t('common.accommodations.compareRooms'),
            bgColor: COLORS.WHITE,
            buttonLeft: {
              iconName: ICONS.ARROW_TAIL_LEFT,
              label: t('common.backNavigation'),
              onClick: () => {
                setIsLayerOpened(false);
              },
            },
            closeLabel: t('common.close'),
            hasAnimateEnter: true,
            hasContainerHeader: true,
            header: (
              <Title
                {...asWidgetProps<ComponentProps<typeof Title>>({
                  className: 'justify-center',
                  theme: 'title-brand-n3',
                })}
              >
                {t('common.accommodations.compareRooms')}
              </Title>
            ),
            onClose: () => {
              setIsLayerOpened(false);
            },
          })}
        >
          {isDesktop ? (
            <ProductRoomsComparatorLarge
              {...asWidgetProps<ComponentProps<typeof ProductRoomsComparatorLarge>>({
                columns: columns,
                availabilityBoards: availabilityBoards,
                allCategoriesItems: allCategoriesItems,
                accommodationCategories: accommodationCategories,
              })}
            />
          ) : (
            <ProductRoomsComparatorNarrow
              {...asWidgetProps<ComponentProps<typeof ProductRoomsComparatorNarrow>>({
                columns: columns,
                availabilityBoards: availabilityBoards,
                allCategoriesItems: allCategoriesItems,
                accommodationCategories: accommodationCategories,
              })}
            />
          )}
        </Layer>
      )}
    </Fragment>
  );
};
